import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Wrench, GraduationCap, HeartHandshake, Users, FileText, Download, ExternalLink, Loader2, Star, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { hcApi } from './api';

export const CAT_STYLE = {
  career_employment: { icon: Briefcase, color: '#922A8C', soft: '#F3E6F2' },
  workforce_tools: { icon: Wrench, color: '#2F9E44', soft: '#E6F4EA' },
  training_education: { icon: GraduationCap, color: '#E8890C', soft: '#FDF1E0' },
  supportive_services: { icon: HeartHandshake, color: '#D6336C', soft: '#FBE6EE' },
  community: { icon: Users, color: '#0E8C7A', soft: '#E1F4F1' },
  forms_documents: { icon: FileText, color: '#3B5BDB', soft: '#E7ECFB' },
};

const fileSize = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const download = async (r, setBusy) => {
  setBusy(true);
  try {
    const res = await hcApi.get(`/resources/${r.id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = r.file_name || 'document'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    toast.error('Could not download this document. Please try again.');
  } finally {
    setBusy(false);
  }
};

export const CatIcon = ({ category, size = 'md' }) => {
  const s = CAT_STYLE[category] || CAT_STYLE.forms_documents;
  const Icon = s.icon;
  const box = size === 'lg' ? 'w-14 h-14 sm:w-16 sm:h-16 rounded-2xl' : 'w-11 h-11 rounded-xl';
  return (
    <span className={`${box} shrink-0 flex items-center justify-center shadow-sm`} style={{ background: s.soft, color: s.color }}>
      <Icon className={size === 'lg' ? 'w-7 h-7' : 'w-5 h-5'} />
    </span>
  );
};

export const ResourceAction = ({ r, full, idPrefix = 'hc-resource' }) => {
  const [busy, setBusy] = useState(false);
  const cls = `h-11 px-5 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors ${full ? 'w-full' : ''}`;
  if (r.builtin) {
    return (
      <Link to={r.url} className={`${cls} bg-[#922A8C] text-white hover:bg-[#7a2275]`} data-testid={`${idPrefix}-open-${r.id}`}>
        Open Form <ClipboardList className="w-4 h-4" />
      </Link>
    );
  }
  if (r.resource_type === 'link') {
    return (
      <a href={r.url} target="_blank" rel="noopener noreferrer" className={`${cls} border border-[#922A8C] text-[#922A8C] hover:bg-[#922A8C] hover:text-white`} data-testid={`${idPrefix}-open-${r.id}`}>
        Open Link <ExternalLink className="w-4 h-4" />
      </a>
    );
  }
  return (
    <button onClick={() => download(r, setBusy)} disabled={busy} className={`${cls} bg-[#922A8C] text-white hover:bg-[#7a2275] disabled:opacity-60`} data-testid={`${idPrefix}-download-${r.id}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download{r.file_size ? ` (${fileSize(r.file_size)})` : ''}
    </button>
  );
};

export const FeaturedCard = ({ r, catName }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3 min-w-[260px] sm:min-w-0 snap-start" data-testid={`hc-featured-resource-${r.id}`}>
    <div className="flex items-start gap-3">
      <CatIcon category={r.category} />
      <div className="min-w-0">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#922A8C]"><Star className="w-3 h-3 fill-current" />Featured</span>
        <h3 className="font-bold text-[#363636] leading-snug">{r.title}</h3>
        <p className="text-xs text-gray-500">{catName}</p>
      </div>
    </div>
    {r.description && <p className="text-sm text-gray-600 line-clamp-3">{r.description}</p>}
    <div className="mt-auto"><ResourceAction r={r} full idPrefix="hc-featured-resource" /></div>
  </div>
);

export const ResourceRow = ({ r, catName }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5" data-testid={`hc-resource-${r.id}`}>
    <div className="flex items-start sm:items-center gap-4 flex-1 min-w-0">
      <CatIcon category={r.category} size="lg" />
      <div className="min-w-0">
        <h3 className="font-bold text-[#363636] text-base sm:text-lg leading-snug" data-testid={`hc-resource-title-${r.id}`}>{r.title}</h3>
        {r.description && <p className="text-sm text-gray-500 mt-0.5">{r.description}</p>}
        <p className="text-xs text-gray-400 mt-1">{catName}{r.states.length ? ` · ${r.states.join(', ')}` : ''}</p>
      </div>
    </div>
    <div className="sm:shrink-0 w-full sm:w-auto"><ResourceAction r={r} full /></div>
  </div>
);
